const TAX_SYSTEMS = [
  { id: 'vat20', percent: 20 },
  { id: 'vat22', percent: 22 },
  { id: 'novat', percent: 0 },
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const remain = (sum, paid) => round2((Number(sum) || 0) - (Number(paid) || 0));

function summarizeExtras(rows) {
  let vat = 0, vatPaid = 0;
  let noVat = 0, noVatPaid = 0;
  let cash = 0, cashPaid = 0;
  for (const r of rows) {
    const sum = Number(r.sum) || 0;
    const paid = Number(r.paid) || 0;
    if (typeof r.payment_form === 'string' && r.payment_form.startsWith('С НДС')) { vat += sum; vatPaid += paid; }
    else if (r.payment_form === 'Без НДС') { noVat += sum; noVatPaid += paid; }
    else if (r.payment_form === 'Наличные') { cash += sum; cashPaid += paid; }
  }
  return {
    extrasVat: round2(vat), extrasVatPaid: round2(vatPaid),
    extrasNoVat: round2(noVat), extrasNoVatPaid: round2(noVatPaid),
    extrasCash: round2(cash), extrasCashPaid: round2(cashPaid),
  };
}

function computeMetrics(card) {
  const sheets = card?.sheets || {};
  const summary = sheets.summary || {};
  const staffRows = Array.isArray(sheets.staff) ? sheets.staff : [];
  const tmcRows = Array.isArray(sheets.tmc) ? sheets.tmc : [];
  const amortRows = Array.isArray(sheets.amortization) ? sheets.amortization : [];
  const extraRows = Array.isArray(sheets.extraCosts) ? sheets.extraCosts : [];
  const deliveredTotals = card?.deliveredTotals || {};
  const deliveredTmcTotal = Number(deliveredTotals.deliveredTmcTotal) || 0;

  const taxSystemId = summary.taxSystemId ?? 'vat20';
  const taxSystem = TAX_SYSTEMS.find((t) => t.id === taxSystemId) || TAX_SYSTEMS[0];
  const isNoVat = taxSystem.id === 'novat' || Number(taxSystem.percent) === 0;

  const vatPctDefault = Number.isFinite(Number(taxSystem.percent)) ? Number(taxSystem.percent) : 20;

  const params = {
    ndflPct: Number(summary.ndflPct ?? 13),
    insPct: Number(summary.insPct ?? 30.5),
    tmcVatPct: Number(summary.tmcVatPct ?? vatPctDefault),
    extrasVatPct: Number(summary.extrasVatPct ?? vatPctDefault),
    vatPayPct: Number(summary.vatPayPct ?? vatPctDefault),
    overheadPct: Number(summary.overheadPct ?? 5),
    profitTaxPct: Number(summary.profitTaxPct ?? (isNoVat ? 0 : 25)),
    subcontractVatPct: Number(summary.subcontractVatPct ?? vatPctDefault),
    soapShareFrac: Number(summary.soapShareFrac ?? (isNoVat ? 1 : 0.85)),
    soapTaxesPct: Number(summary.soapTaxesPct ?? (isNoVat ? 12 : 17)),
    breakEven: Number(summary.breakEven ?? 0),
    tmcPaidManual: Number(summary.tmcPaidManual ?? 0),
  };

  const staffTotal = staffRows.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const staffCashSum = staffRows.reduce((s, r) => s + (Number(r.cash_total) || 0), 0);
  const staffCardSum = staffRows.reduce((s, r) => s + (Number(r.card_total) || 0), 0);
  const staffCashPaid = staffRows.reduce((s, r) => s + (Number(r.cash_paid) || 0), 0);
  const staffCardPaid = staffRows.reduce((s, r) => s + (Number(r.card_paid) || 0), 0);

  const plannedTmcTotal = tmcRows.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const tmcBuyTotal = deliveredTmcTotal > 0 ? deliveredTmcTotal : plannedTmcTotal;
  const tmcSalesTotal = tmcRows.reduce((s, r) => s + (r.for_sale ? (Number(r.sale_sum) || 0) : 0), 0);

  const amortMonth = amortRows.reduce((s, r) => s + (Number(r.amort_per_month) || 0), 0);
  const amortPurchaseTotal = amortRows.reduce((s, r) => s + (Number(r.total) || 0), 0);

  const extras = summarizeExtras(extraRows);

  const manualContracts = Array.isArray(summary.contractRows) && summary.contractRows.length > 0
    ? summary.contractRows
    : [];
  const manualContractsSum = manualContracts.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const contractTotal = round2(manualContractsSum + (tmcSalesTotal > 0 ? tmcSalesTotal : 0));

  const C2 = round2(staffTotal);
  const C3 = round2(staffCashSum), D3 = round2(staffCashPaid), E3 = remain(C3, D3);
  const C4 = round2(staffCardSum), D4 = round2(staffCardPaid), E4 = remain(C4, D4);
  const C5 = round2(C4 * params.ndflPct / (100 - params.ndflPct));
  const C6 = round2((C4 + C5) * params.insPct / 100);
  const C7 = round2(C5 + C6);
  const C8 = round2(tmcBuyTotal), D8 = round2(params.tmcPaidManual), E8 = remain(C8, D8);
  const C9 = round2(amortMonth);
  const C10 = extras.extrasVat, D10 = extras.extrasVatPaid, E10 = remain(C10, D10);
  const C11 = extras.extrasNoVat, D11 = extras.extrasNoVatPaid, E11 = remain(C11, D11);
  const C12 = extras.extrasCash, D12 = extras.extrasCashPaid, E12 = remain(C12, D12);

  const C23 = isNoVat
    ? round2(contractTotal * params.soapShareFrac)
    : round2(Math.max(0, (contractTotal - (C4 + C7 + C8 + C10 + C11)) * params.soapShareFrac));

  const C25_calc = round2(contractTotal * params.vatPayPct / (100 + params.vatPayPct));
  const C26_calc = round2(
    C8  * params.tmcVatPct         / (100 + params.tmcVatPct) +
    C10 * params.extrasVatPct      / (100 + params.extrasVatPct) +
    C23 * params.subcontractVatPct / (100 + params.subcontractVatPct)
  );
  const C25 = isNoVat ? 0 : C25_calc;
  const C26 = isNoVat ? 0 : C26_calc;
  const C13 = isNoVat ? 0 : round2(C25_calc - C26_calc);

  const C14 = round2(contractTotal * params.overheadPct / 100);
  const C16 = round2(C23 * params.soapTaxesPct / 100);

  const profitPct = params.profitTaxPct;
  const BASE_wo_C15 = round2(C2 + C7 + C8 + C9 + C10 + C11 + C12 + C13 + C14 + C16);
  const C15 = isNoVat ? 0 : round2((contractTotal - BASE_wo_C15) * profitPct / (100 + profitPct));
  const C17 = round2(BASE_wo_C15 + C15);
  const C18 = round2(contractTotal - C17);
  const C19 = contractTotal > 0 ? round2((C18 * 100) / contractTotal) : 0;
  const C20 = round2(amortPurchaseTotal);
  const C21 = round2(params.breakEven);
  const E22 = round2(E3 + E12);

  const C24 = round2(C23 * (100 - params.soapTaxesPct) / 100);
  const C28 = round2(C4 + C7 + C8 + C9 + C10 + C11 + C13 + C23);
  const C27 = round2(contractTotal - C28);

  const profitOnHandRaw = round2(C24 - C3 - C12 - C14);
  const jTotal = isNoVat
    ? round2(C24 - C2 - C7 - C8 - C9 - C10 - C11 - C12 - C14)
    : profitOnHandRaw;
  const profitOnHandPct = isNoVat
    ? C19
    : contractTotal > 0
    ? round2((profitOnHandRaw * 100) / contractTotal)
    : 0;

  return {
    contractTotal,
    fot_total: C2,
    fot_cash_sum: C3, fot_cash_paid: D3, fot_cash_remain: E3,
    fot_card_sum: C4, fot_card_paid: D4, fot_card_remain: E4,
    ndfl: C5,
    insurance: C6,
    fot_tax: C7,
    tmc_with_vat: C8, tmc_paid_manual: D8, tmc_remain: E8,
    amort_month: C9,
    extras_vat: C10, extras_vat_paid: D10, extras_vat_remain: E10,
    extras_no_vat: C11, extras_no_vat_paid: D11, extras_no_vat_remain: E11,
    extras_cash: C12, extras_cash_paid: D12, extras_cash_remain: E12,
    vat_to_pay: C25,
    vat_deduction: C26,
    vat_delta: C13,
    overhead: C14,
    profit_tax: C15,
    cost_price: C17,
    net_profit: C18,
    profitability_pct: C19,
    amort_purchase_total: C20,
    break_even: C21,
    cash_remain_total: E22,
    subcontract_base: C23,
    soap_profit_on_hand: C24,
    vat_base_total: C27,
    base_total: C28,
    profit_on_hand: jTotal,
    profit_on_hand_raw: profitOnHandRaw,
    profit_on_hand_pct: profitOnHandPct,
    soap_share_frac: params.soapShareFrac,
    taxSystemId,
  };
}

export default computeMetrics;
export { computeMetrics };
